import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { getAccount, setBattleType, setSeasonNumber } from '~/Actions/ActionApp';
import BattleTypePicker from '~/Components/BattleTypePicker/BattleTypePicker';
import Charts from '~/Components/Charts/Charts';
import Hidden from '~/Components/Hidden/Hidden';
import Message from '~/Components/Message/Message';
import NotFound from '~/Components/NotFound/NotFound';
import Header from '~/Components/ProfileHeader/ProfileHeader';
import ProfileStatistics from '~/Components/ProfileStatistics/ProfileStatistics';
import ProfileStatisticsHeader from '~/Components/ProfileStatisticsHeader/ProfileStatisticsHeader';
import SeasonPicker from '~/Components/SeasonPicker/SeasonPicker';
import ShipsTable from '~/Components/ShipsTable/ShipsTable';
import { RANK_BATTLE_TYPE_NAMES } from '~/constants';
import { State } from '~/Reducers';
import { getAvailableBattleTypes } from '~/utils/statistics';

import styles from './Profile.scss';
import { AppDispatch } from '~/appDispatch';

export interface ProfileState {
  vehicleTypesIndexMap: VehicleTypesIndexMap;
  vehiclesMap: VehiclesMap;
  account?: Account;
  clanData?: ClanData;
  statisticsData: Array<TableShip>;
  battleType: StatBattleType;
  battleTypeRaw: BattleTypeName;
  seasonPickerVisible: boolean;
  sortField: TableSortField;
  sortDirection: SortDirection;
  seasonNumber?: string;
  filters: Array<string>;
  fetching: FetchingMap;
}

const stateSelector = (state: State): ProfileState => {
  return {
    vehicleTypesIndexMap: state.ReducerApp.vehicleTypesIndexMap,
    vehiclesMap: state.ReducerApp.vehiclesMap,
    account: state.ReducerApp.account,
    clanData: state.ReducerApp.clanData,
    statisticsData: state.ReducerApp.statisticsData,
    battleType: state.ReducerApp.battleType,
    battleTypeRaw: state.ReducerApp.battleTypeRaw,
    seasonPickerVisible: state.ReducerApp.seasonPickerVisible,
    sortField: state.ReducerApp.sortField,
    sortDirection: state.ReducerApp.sortDirection,
    seasonNumber: state.ReducerApp.seasonNumber,
    filters: state.ReducerApp.filters,
    fetching: state.ReducerApp.fetching,
  };
};

const Profile = () => {
  const state = useSelector<State, ProfileState>(stateSelector, equal);
  const params = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const {
    vehicleTypesIndexMap,
    vehiclesMap,
    account,
    statisticsData,
    battleType,
    battleTypeRaw,
    seasonPickerVisible,
    sortField,
    sortDirection,
    seasonNumber,
    filters,
    fetching,
  } = state;

  useEffect(() => {
    if (params.id && (!account || account.id !== params.id)) {
      dispatch(getAccount(params.id, params.access_code));
    }
    if (params.battletype && params.battletype !== battleTypeRaw) {
      dispatch(setBattleType(params.battletype as LocationParams['battletype']));
    }
    if (params.season && params.season !== seasonNumber) {
      dispatch(setSeasonNumber(params.season));
    }
    if (!params.season && ['rank', 'rating'].includes(params.battletype)) {
      dispatch(setSeasonNumber('all'));
    }
  }, [params]);

  if (fetching.account) {
    return null;
  }

  if (!account && fetching.account === false) {
    return <NotFound />;
  }

  const statBattleTypes = getAvailableBattleTypes(account, false);
  if (account && statBattleTypes.length > 0 && !statBattleTypes.includes(battleTypeRaw)) {
    return <Navigate to={`/statistics/${account.id}/${statBattleTypes[0]}/`} />;
  }

  const filtersMap: FiltersMap = {};

  filters.forEach((filterStr) => {
    const key = filterStr.split('#')[0];
    const val = filterStr.split('#')[1];

    if (filtersMap[key]) {
      filtersMap[key].push(val);
    } else {
      filtersMap[key] = [val];
    }
  });

  const filteredStatisticsData = statisticsData.filter((item) => {
    let enabled = true;
    if (item[battleType] && Object.keys(item[battleType]).length === 0 && !seasonPickerVisible) {
      enabled = false;
    }

    if (seasonPickerVisible && seasonNumber !== 'all') {
      enabled = false;
      if (Object.values(item.seasons).length !== 0) {
        let data;

        if (RANK_BATTLE_TYPE_NAMES.includes(battleTypeRaw)) {
          // index "-1" is contained in old seasons
          const key = Object.keys(item.seasons[seasonNumber] ?? {}).includes('-1') ? '-1' : '0';
          data = get(item, `seasons.${seasonNumber}[${key}]${battleType}`, undefined);
        } else if (battleTypeRaw === 'rating') {
          data = get(item, `seasons.${seasonNumber}.${battleType}`, undefined);
        }

        enabled = Object.keys(data ?? {}).length > 0;
      }
    }
    return enabled;
  });

  const filteredMappedByShipStatisticsData = filteredStatisticsData.reduce((result, currData) => {
    result[currData.id] = currData;

    return result;
  }, {} as Record<TableShip['id'], TableShip>);

  const filteredShips: Array<TableShipStat> = filteredStatisticsData
    .filter((ship) => {
      const suitableBattleType = ship[battleType] ? Object.keys(ship[battleType]).length > 0 : false;
      if (Object.keys(filtersMap).length > 0) {
        const shipInfo = vehiclesMap[ship.id];
        const filterResults = [];
        for (const key in filtersMap) {
          const checkingVal = get(shipInfo, key, undefined);
          const filterVal = get(filtersMap, `${key}.0`, undefined);

          if (
            (checkingVal && filtersMap[key].includes(`${checkingVal}`)) ||
            (key === 'title' && filterVal && `${checkingVal}`.toLowerCase().includes(`${filterVal}`.toLowerCase()))
          ) {
            filterResults.push(true);
          } else {
            filterResults.push(false);
          }
        }
        return suitableBattleType && filterResults.filter((v) => !!v).length === filterResults.length;
      } else {
        return suitableBattleType;
      }
    })
    .map((ship) => {
      return {
        id: ship.id,
        ...ship[battleType],
      };
    });

  const allFilteredShips: Array<TableShipStat> = filteredStatisticsData.map((ship) => {
    return {
      id: ship.id,
      ...ship[battleType],
    };
  });

  const sortedShips: Array<TableShipStat> = filteredShips.sort((a: TableShipStat, b: TableShipStat) => {
    const aShip = vehiclesMap[a.id];
    const bShip = vehiclesMap[b.id];

    if (aShip && bShip) {
      const aTypeIndex = vehicleTypesIndexMap[aShip.type.name];
      const bTypeIndex = vehicleTypesIndexMap[bShip.type.name];

      switch (sortField) {
        case 'class': {
          return (bTypeIndex - aTypeIndex) * sortDirection;
        }
        case 'nation': {
          const aName = get(aShip, 'nation.name', '');
          const bName = get(bShip, 'nation.name', '');
          return aName.localeCompare(bName) * sortDirection;
        }
        case 'level': {
          return (bShip.level - aShip.level) * sortDirection;
        }
        case 'name': {
          const aName = get(aShip, 'title', '') || '';
          const bName = get(bShip, 'title', '') || '';
          return aName.localeCompare(bName) * sortDirection;
        }
        case 'battles_count': {
          return (b.battles_count - a.battles_count) * sortDirection;
        }
        case 'premium_exp': {
          return (b.premium_exp - a.premium_exp) * sortDirection;
        }
        case 'wins': {
          return (b.wins - a.wins) * sortDirection;
        }

        case 'mastery': {
          return (
            ((filteredMappedByShipStatisticsData[b.id].mastery.rank || 0) -
              (filteredMappedByShipStatisticsData[a.id].mastery.rank || 0)) *
            sortDirection
          );
        }

        default: {
          return (b.battles_count - a.battles_count) * sortDirection;
        }
      }
    } else {
      return 0;
    }
  });

  const ranksLeaguePriority: League[] = ['gold', 'silver', 'bronze'];
  const ratingsLeaguePriority: League[] = ['platinum', 'gold', 'silver', 'bronze', 'iron'];

  function findSeasonalBestRankOrRating(
    data: SprintRankData[] | RatingData[] | SeasonBestRankInfo[],
    seasonMode: 'season' | 'all',
    battleTypeMode: 'rank' | 'rating',
    seasonLabel = 'all',
  ): SeasonBestRankInfo | SeasonBestRatingInfo {
    if (battleTypeMode === 'rank') {
      return findBestRankOfSeason(data as SprintRankData[], seasonMode, seasonLabel);
    }
    if (battleTypeMode === 'rating') {
      return findBestRatingOfSeason(data as RatingData[], seasonLabel);
    }
  }

  /**
   * Filter out invalid indexes (0, -1 etc. - anything other than 1(gold), 2(silver) or 3(bronze)) -> resultIndexes.
   * Remove unnecessary data according to these indexes -> resultSprintLeaguesArray.
   * Use the result to correctly determine the best rank and league.
   * @param sprintLeaguesArray
   * @param indexes
   */
  function getUpdatedLeagueData(sprintLeaguesArray: LeagueData[], indexes: string[]): UpdatedLeagueDataResult {
    const resultSprintLeaguesArray = [...sprintLeaguesArray];
    const resultIndexes = [...indexes];

    for (let i = resultIndexes.length - 1; i >= 0; i--) {
      const num = resultIndexes[i];

      if (![1, 2, 3].includes(Number(num))) {
        if (i >= 0 && i < resultSprintLeaguesArray.length) {
          resultSprintLeaguesArray.splice(i, 1);
          resultSprintLeaguesArray.splice(i, 1);
        }
      }
    }

    return { resultSprintLeaguesArray, resultIndexes };
  }

  function findBestRankOfSeason(
    data: SprintRankData[] | SeasonRankData[],
    seasonMode: 'season' | 'all',
    seasonLabel?: string,
  ): SeasonBestRankInfo {
    const leagues: Record<League, number[]> = {
      gold: [],
      silver: [],
      bronze: [],
      iron: [],
      platinum: [],
    };

    if (seasonMode === 'season') {
      (data as Record<string, SprintRankData>[]).forEach((sprint) => {
        Object.values(sprint).forEach((sprintRankData) => {
          const updatedLeagueData = getUpdatedLeagueData(Object.values(sprintRankData), Object.keys(sprintRankData));

          const league = determineLeague(updatedLeagueData.resultIndexes[0]);
          if (!league) return;

          const bestLeagueInfo = updatedLeagueData.resultSprintLeaguesArray[0];
          if (bestLeagueInfo.rank_best && bestLeagueInfo.rank_best > 0) {
            leagues[league].push(bestLeagueInfo.rank_best);
          }
        });
      });
    } else if (seasonMode === 'all') {
      (data as SeasonRankData[]).forEach((seasonData) => {
        if ('league' in seasonData && 'best_rank' in seasonData) {
          const league = seasonData.league;
          const bestRank = seasonData.best_rank;

          if (bestRank && bestRank > 0 && league && leagues[league]) {
            leagues[league].push(bestRank);
          }
        }
      });
    }

    return findBestRankResult(leagues, seasonLabel);
  }

  function findBestRatingOfSeason(ratings: RatingData[], seasonLabel?: string): SeasonBestRatingInfo {
    if (ratings.length === 0) {
      return { best_rating: null, league: null, ...(seasonLabel && { season: seasonLabel }) };
    }

    const leagues: Partial<Record<League, number[]>> = {};

    ratings.forEach((rating) => {
      const league = rating.league_best;
      const ratingValue = rating.league_best_rating;

      if (!leagues[league]) {
        leagues[league] = [];
      }
      leagues[league]?.push(ratingValue);
    });

    for (const league of ratingsLeaguePriority) {
      if (leagues[league]?.length > 0) {
        return {
          best_rating: Math.max(...leagues[league]),
          league,
          ...(seasonLabel && { season: seasonLabel }),
        };
      }
    }

    return { best_rating: null, league: null, ...(seasonLabel && { season: seasonLabel }) };
  }

  function determineLeague(bestLeagueIndex: string): League | null {
    const leagueIndexesMap: Record<string, League> = {
      '1': 'gold',
      '2': 'silver',
      '3': 'bronze',
    } as const;

    return bestLeagueIndex in leagueIndexesMap ? leagueIndexesMap[bestLeagueIndex] : null;
  }

  function findBestRankResult(leagues: Record<League, number[]>, seasonLabel?: string): SeasonBestRankInfo {
    for (const league of ranksLeaguePriority) {
      if (leagues[league].length > 0) {
        const bestRank = Math.min(...leagues[league]);
        return {
          best_rank: bestRank,
          league,
          ...(seasonLabel && { season: seasonLabel }),
        };
      }
    }

    return {
      best_rank: null,
      league: null,
      ...(seasonLabel && { season: seasonLabel }),
    };
  }

  function findBestRankFromAllSeasons(results: SeasonBestRankInfo[], seasonLabel = 'all'): SeasonBestRankInfo {
    if (!results.length) {
      return { best_rank: null, league: null, season: seasonLabel };
    }

    for (const league of ranksLeaguePriority) {
      const leagueResults = results.filter((result) => result.league === league);
      if (leagueResults.length > 0) {
        // Find minimum (best) rank in current league
        const best = leagueResults.reduce((best, current) => (current.best_rank < best.best_rank ? current : best));
        return { ...best, season: seasonLabel };
      }
    }
  }

  function findBestRatingFromResults(results: SeasonBestRatingInfo[], seasonLabel = 'all'): SeasonBestRatingInfo {
    if (!results.length) {
      return null;
    }

    for (const league of ratingsLeaguePriority) {
      const leagueResults = results.filter((result) => result.league === league);
      if (leagueResults.length > 0) {
        // Find maximum (best) rating in current league
        const best = leagueResults.reduce((best, current) => (current.best_rating > best.best_rating ? current : best));
        return { ...best, season: seasonLabel };
      }
    }
  }

  let statistics = get(account, `statistics[${battleType}]`, undefined);
  const seasonId = parseInt(seasonNumber, 10);
  const seasonData = get(account, `statistics.seasons[${seasonId}]`, undefined) as
    | Record<number, Record<StatBattleType, BattleStat>>
    | Record<StatBattleType, BattleStat>
    | undefined;

  const rankInfoStatistics = get(account, `statistics[rank_info]`, undefined);
  const hasRankStatistics = !!rankInfoStatistics && Object.keys(rankInfoStatistics).length > 0;
  const ratingInfoStatistics = get(account, `statistics[rating_info]`, undefined);
  const hasRatingStatistics = !!ratingInfoStatistics && Object.keys(ratingInfoStatistics).length > 0;
  const seasonsAndBestRanksData: SeasonBestRankInfo[] = [];
  const seasonsAndBestRatingData: SeasonBestRatingInfo[] = [];
  if (hasRankStatistics) {
    const seasonsContent = Object.values(rankInfoStatistics);
    const seasonsNumbers = Object.keys(rankInfoStatistics);

    seasonsNumbers.forEach((seasonsNumber, index) => {
      const { best_rank, league } = findSeasonalBestRankOrRating(
        [seasonsContent[index] ?? {}] as SprintRankData[],
        'season',
        'rank',
      ) as SeasonBestRankInfo;
      if (!best_rank) return;
      seasonsAndBestRanksData.push({
        season: seasonsNumber,
        best_rank: best_rank,
        league: league,
      });
    });

    const theBestRank = findBestRankFromAllSeasons(seasonsAndBestRanksData);
    seasonsAndBestRanksData.push(theBestRank);
  }

  if (hasRatingStatistics) {
    const seasonNumbers = Object.keys(ratingInfoStatistics);

    seasonNumbers.forEach((seasonNumber) => {
      const seasonData = ratingInfoStatistics[seasonNumber];
      if (seasonData && !!seasonData.league_best) {
        const bestRating = seasonData.league_best_rating || 1;
        seasonsAndBestRatingData.push({
          season: seasonNumber,
          best_rating: bestRating,
          league: seasonData.league_best,
        });
      }
    });

    const theBestRating = findBestRatingFromResults(seasonsAndBestRatingData, 'all');
    if (!!seasonsAndBestRatingData.length) {
      seasonsAndBestRatingData.push(theBestRating);
    }
  }

  if (seasonPickerVisible && seasonData) {
    if (Object.keys(seasonData).some((key) => !isNaN(Number(key))) && battleTypeRaw === 'rank') {
      const rankSeasonData = seasonData as Record<number, Record<StatBattleType, BattleStat>>;
      statistics = rankSeasonData[0]?.[battleType] ?? Object.values(rankSeasonData)[0]?.[battleType];
    } else if (battleTypeRaw === 'rating') {
      const ratingSeasonData = seasonData as Record<StatBattleType, BattleStat>;
      statistics = ratingSeasonData?.[battleType] ?? ratingSeasonData?.[battleType];
    }
  }

  const hasStatistics = !!statistics && Object.keys(statistics).length > 0;
  const isHidden = account ? account.hidden_profile : false;

  if (isHidden) {
    return <Hidden />;
  }

  const renderProfileHeader = () => {
    if (battleTypeRaw === 'rank' && hasRankStatistics) {
      return (
        <ProfileStatisticsHeader
          statistics={statistics}
          seasonsAndBestRanksData={seasonsAndBestRanksData}
          seasonNumber={state.seasonNumber}
        />
      );
    } else if (battleTypeRaw === 'rating' && hasRatingStatistics && !!seasonsAndBestRatingData.length) {
      return (
        <ProfileStatisticsHeader
          statistics={statistics}
          seasonsAndBestRatingData={seasonsAndBestRatingData}
          seasonNumber={state.seasonNumber}
        />
      );
    } else {
      return <ProfileStatisticsHeader statistics={statistics} />;
    }
  };

  return (
    <>
      <div className={styles.headerWrapper}>
        <Header />
      </div>
      <div className={styles.pickerRow}>
        <BattleTypePicker withDivisionPicker />
        {seasonPickerVisible && (
          <div className={styles.seasonPicker}>
            <SeasonPicker />
          </div>
        )}
      </div>
      {hasStatistics && (
        <>
          {renderProfileHeader()}
          <ProfileStatistics statistics={statistics} />
          <Charts />
          <ShipsTable sortedShips={sortedShips} allShips={allFilteredShips} />
        </>
      )}
      {account && !hasStatistics && (
        <Message
          text={t('Ты сможешь просмотреть боевую статистику этого игрока, когда игрок сыграет свой первый бой.')}
        />
      )}
    </>
  );
};

export default memo(Profile);
